import type { JSX, ReactNode } from "react";

import { useTenantBranding } from "@/api/queries/branding";
import { NotFound } from "@/shell/lazyPages";
import {
  isPageVisible,
  type ToggleablePageKey,
} from "@/shell/toggleablePages";

interface Props {
  pageKey: ToggleablePageKey;
  children: ReactNode;
}

/**
 * Gate a tenant-shell route on per-tenant page visibility.
 *
 * Without this wrapper a hidden page would still be reachable via
 * direct URL (the nav filter only hides the link). When the tenant
 * has disabled the page we render the standard NotFound page so the
 * URL surface mirrors the visible nav.
 *
 * Branding query is shared with the rest of the tenant shell, so this
 * adds no extra request — it only reads the cached value.
 */
export function RequirePageEnabled({
  pageKey,
  children,
}: Props): JSX.Element {
  const branding = useTenantBranding();
  if (branding.isLoading) {
    return <></> as unknown as JSX.Element;
  }
  if (!isPageVisible(branding.data?.pages_visibility, pageKey)) {
    return <NotFound />;
  }
  return <>{children}</>;
}
